import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, Avatar, Divider, Alert, CircularProgress, Paper, Grid, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Description as DocumentIcon, Assignment as TaskIcon, History as HistoryIcon, Person as PersonIcon, Schedule as ScheduleIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Forward as ForwardIcon, Edit as EditIcon, Visibility as ViewIcon, Download as DownloadIcon, Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { limitedAdminAPI } from '../../api/limitedAdminAPI';
import { useDateFormat, DATE_FORMATS } from '../../context/DateFormatContext';
import { formatShamsiDate, formatGregorianDate, formatHijriDate } from '../../utils/dateUtils';

const LimitedAdminDocumentFlowViewer = () => {
  const { t, i18n } = useTranslation();
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { dateFormat } = useDateFormat();
  const isRtl = ['dari', 'ps'].includes(i18n.language);
  const [documentFlow, setDocumentFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  const [taskAssignments, setTaskAssignments] = useState([]);

  useEffect(() => {
    fetchDocumentFlow();
  }, [documentId]);

  const fetchDocumentFlow = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both document flow and task assignments
      const [documentFlowData, taskAssignmentsData] = await Promise.all([
        limitedAdminAPI.getDocumentFlow(documentId),
        limitedAdminAPI.getTaskAssignments()
      ]);
      
      setDocumentFlow(documentFlowData.data.data);
      
      // Extract task assignments
      const assignments = taskAssignmentsData.data?.data?.taskAssignments || taskAssignmentsData.data?.taskAssignments || [];
      setTaskAssignments(assignments);
      
    } catch (err) {
      console.error('Error fetching document flow:', err);
      setError(err.message || 'Failed to load document flow');
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (event) => {
    switch (event.action) {
      case 'CREATED':
        return <DocumentIcon />;
      case 'ASSIGNED':
        return <TaskIcon />;
      case 'RECEIVED':
        return <CheckCircleIcon />;
      case 'FORWARDED':
        return <ForwardIcon />;
      case 'STATUS_CHANGED':
        return <EditIcon />;
      case 'TASK_ASSIGNED':
        return <TaskIcon />;
      default:
        return <HistoryIcon />;
    }
  };

  const getEventColor = (event) => {
    switch (event.action) {
      case 'CREATED':
        return 'primary';
      case 'ASSIGNED':
      case 'TASK_ASSIGNED':
        return 'info';
      case 'RECEIVED':
        return 'success';
      case 'FORWARDED':
        return 'warning';
      case 'STATUS_CHANGED':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getLocalizedDescription = (event) => {
    const { action, description, user, metadata } = event;
    
    // Handle specific English descriptions that might come from the API
    if (description) {
      if (description.includes('Comment added to document')) {
        return t('limitedadmin:comment_added_to_document', 'Comment added to document');
      }
      if (description.includes('Document physically received by')) {
        const userMatch = description.match(/Document physically received by (.+)/);
        if (userMatch) {
          return t('limitedadmin:document_physically_received_by', { user: userMatch[1] });
        }
      }
      if (description.includes('Document assigned to user:')) {
        const userMatch = description.match(/Document assigned to user: (.+)/);
        if (userMatch) {
          return t('limitedadmin:document_assigned_to_user', { user: userMatch[1] });
        }
      }
      if (description.includes('forwarded the task to')) {
        const match = description.match(/(.+) forwarded the task to (.+)/);
        if (match) {
          return t('limitedadmin:forwarded_the_task_to', { user: match[1], target: match[2] });
        }
      }
      if (description.includes('Task completed by')) {
        const userMatch = description.match(/Task completed by (.+)/);
        if (userMatch) {
          return t('limitedadmin:task_completed_by', { user: userMatch[1] });
        }
      }
    }
    
    switch (action) {
      case 'CREATED':
        return t('limitedadmin:document_created');
      case 'ASSIGNED':
        if (metadata?.assigned_to_user || metadata?.assignee_name) {
          return t('limitedadmin:document_assigned_to_user', { user: metadata.assigned_to_user || metadata.assignee_name });
        }
        return t('limitedadmin:document_assigned');
      case 'RECEIVED':
        if (metadata?.received_by_user) {
          return t('limitedadmin:document_physically_received_by', { user: metadata.received_by_user });
        }
        return t('limitedadmin:document_received');
      case 'FORWARDED':
        if (metadata?.forwarded_to_user) {
          return t('limitedadmin:forwarded_the_task_to', { 
            user: user?.full_name || user?.username, 
            target: metadata.forwarded_to_user 
          });
        }
        return t('limitedadmin:document_forwarded');
      case 'STATUS_CHANGED':
        if (metadata?.new_status === 'COMPLETED') {
          return t('limitedadmin:task_completed_by', { user: user?.full_name || user?.username });
        }
        return description || t('limitedadmin:status_changed');
      case 'TASK_ASSIGNED':
        return t('limitedadmin:the_task_was_assigned_to', { target: metadata?.assigned_to_user || 'Unknown' });
      default:
        // If we have a description, try to translate common English phrases
        if (description) {
          if (description === 'Document created') return t('limitedadmin:document_created');
          if (description === 'Document assigned') return t('limitedadmin:document_assigned');
          if (description === 'Document received') return t('limitedadmin:document_received');
          if (description === 'Document forwarded') return t('limitedadmin:document_forwarded');
          if (description === 'Task completed') return t('limitedadmin:task_completed_by', { user: user?.full_name || user?.username });
          if (description === 'TASK_ASSIGNED') return t('limitedadmin:task_assigned');
        }
        return description || action;
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setEventDetailsOpen(true);
  };

  const handleCloseEventDetails = () => {
    setSelectedEvent(null);
    setEventDetailsOpen(false);
  };

  // Helper to get latest task assignment status for a document
  const getDocumentTaskStatus = (docId) => {
    if (!taskAssignments || !Array.isArray(taskAssignments)) {
      return null;
    }
    const assignment = taskAssignments.find(t => t.document_id === docId);
    return assignment?.status || null;
  };

  // Helper to get status color for task assignment statuses
  const getTaskStatusColor = (status) => ({
    ASSIGNED: 'default',
    ACCEPTED: 'info',
    RECEIVED: 'info',
    IN_PROGRESS: 'primary',
    COMPLETED: 'success',
    REJECTED: 'error',
    REJECTED_RECEIPT: 'error',
    FORWARDED: 'warning',
    SKIPPED: 'secondary'
  }[status] || 'default');

  // Helper to get status label for task assignment statuses
  const getTaskStatusLabel = (status) => {
    const key = `limitedadmin:${status.toLowerCase()}`;
    const translated = t(key, status);
    return translated;
  };

  // Format date based on selected date format
  const formatDate = (dateString, showTime = false) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    
    return dateFormat === DATE_FORMATS.SHAMSI ? formatShamsiDate(date, isRtl, showTime) :
      dateFormat === DATE_FORMATS.QAMARI ? formatHijriDate(date, isRtl, showTime) :
      formatGregorianDate(date, isRtl, showTime);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('limitedadmin:error_loading_document_flow')}: {error}
        </Alert>
      </Box>
    );
  }

  if (!documentFlow) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          {t('limitedadmin:no_document_flow_found') || 'No document flow found'}
        </Alert>
      </Box>
    );
  }

  const { document, flow } = documentFlow;

  if (!document) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          {t('limitedadmin:no_document_found') || 'No document found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, direction: 'rtl', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ ml: 1, bgcolor: 'white', boxShadow: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1, textAlign: 'right' }}>
           <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 0.5, color: '#1976d2', textAlign: 'right' }}>
             {t('limitedadmin:document_flow')}
           </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'right' }}>
            {document.title} - {document.reference_number}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchDocumentFlow}
          sx={{ ml: 1, bgcolor: 'white', borderColor: '#1976d2', color: '#1976d2' }}
        >
          {t('limitedadmin:refresh')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() => console.log(t('limitedadmin:export_document_flow'))}
          sx={{ bgcolor: 'white', borderColor: '#1976d2', color: '#1976d2' }}
        >
          {t('limitedadmin:download')}
        </Button>
      </Box>

      {/* Document Summary */}
      <Card sx={{ mb: 2, boxShadow: 2, borderRadius: 1 }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
               <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', color: '#1976d2', fontSize: '1.1rem' }}>
                 <DocumentIcon sx={{ ml: 1, fontSize: '1.2rem' }} />
                 {t('limitedadmin:document_details')}
               </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right' }}>{document.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('limitedadmin:title')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right' }}>{document.reference_number}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('limitedadmin:reference_number')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Box sx={{ textAlign: 'right' }}>
                    {(() => {
                      const taskStatus = getDocumentTaskStatus(document.id);
                      if (!taskStatus) {
                        // Fallback to document status if no task assignment
                        return (
                          <Chip
                            label={t(`limitedadmin:${document.status.toLowerCase()}`) || document.status}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        );
                      }
                      
                      // Only use valid task assignment statuses
                      const validTaskStatuses = ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'REJECTED', 'FORWARDED', 'ACCEPTED', 'RECEIVED', 'REJECTED_RECEIPT'];
                      if (!validTaskStatuses.includes(taskStatus)) {
                        return (
                          <Chip
                            label={t(`limitedadmin:${document.status.toLowerCase()}`) || document.status}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        );
                      }
                      
                      return (
                        <Chip
                          label={getTaskStatusLabel(taskStatus)}
                          size="small"
                          color={getTaskStatusColor(taskStatus)}
                          variant="outlined"
                        />
                      );
                    })()}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('limitedadmin:status')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right' }}>{document.type}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('limitedadmin:type')}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
               <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', color: '#1976d2', fontSize: '1.1rem' }}>
                 <PersonIcon sx={{ ml: 1, fontSize: '1.2rem' }} />
                 {t('limitedadmin:people_involved')}
               </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right' }}>
                    {document.creator?.full_name || document.creator?.username || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('limitedadmin:creator')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right' }}>
                    {document.currentAssignee?.full_name || document.currentAssignee?.username || t('limitedadmin:unassigned')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('limitedadmin:current_assignee')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', alignItems: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 0.5, border: '1px solid #e9ecef' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right' }}>
                    {document.created_at ? formatDate(document.created_at, true) : '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
                    {t('limitedadmin:created_at')}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Flow Timeline */}
      <Card sx={{ boxShadow: 2, borderRadius: 1 }}>
        <CardContent>
           <Typography variant="h6" gutterBottom sx={{ mb: 3, textAlign: 'right' }}>
             {t('limitedadmin:document_flow_timeline')}
           </Typography>
          
          {flow && flow.length > 0 ? (
            <Box sx={{ position: 'relative' }}>
              {/* Timeline line */}
              <Box
                sx={{
                  position: 'absolute',
                  right: 20,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  bgcolor: '#1976d2',
                  zIndex: 1
                }}
              />
              
              <List sx={{ p: 0 }}>
                {flow
                  .filter(event => event.type !== 'task' && event.action !== 'TASK_ASSIGNED')
                  .map((event, index) => (
                  <ListItem
                    key={`${event.type}-${event.id}`}
                    sx={{
                      position: 'relative',
                      pr: 5,
                      pb: 1.5,
                      '&:last-child': { pb: 0 }
                    }}
                  >
                    {/* Timeline dot */}
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 14,
                        top: 16,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: `${getEventColor(event)}.main`,
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.6rem'
                      }}
                    >
                      {React.cloneElement(getEventIcon(event), { sx: { fontSize: '0.6rem' } })}
                    </Box>

                    <Paper
                      sx={{
                        p: 1.5,
                        width: '100%',
                        cursor: 'pointer',
                        '&:hover': { 
                          bgcolor: '#f8f9fa',
                          boxShadow: 2
                        },
                        boxShadow: 1,
                        borderRadius: 1,
                        border: '1px solid #e9ecef',
                        transition: 'all 0.2s ease-in-out'
                      }}
                      onClick={() => handleEventClick(event)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexDirection: 'row-reverse', justifyContent: 'space-between' }}>
                          <Avatar
                            src={event.user?.avatar}
                            sx={{ width: 28, height: 28, bgcolor: '#1976d2', fontSize: '0.75rem' }}
                          >
                            {event.user?.full_name?.charAt(0) || event.user?.username?.charAt(0) || 'U'}
                          </Avatar>
                          <Box sx={{ flexGrow: 1, textAlign: 'right', mx: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.25, fontSize: '0.85rem' }}>
                              {event.user?.full_name || event.user?.username || t('limitedadmin:unknown_user')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {event.action}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'row-reverse' }}>
                            <Chip
                              label={t(`limitedadmin:${event.type.toLowerCase()}`)}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', bgcolor: '#e9ecef', px: 0.75, py: 0.25, borderRadius: 0.5 }}>
                              {formatDate(event.created_at, true)}
                            </Typography>
                          </Box>
                        </Box>
                      <Typography variant="body2" sx={{ mb: 0.5, textAlign: 'right', fontSize: '0.8rem', lineHeight: 1.4 }}>
                        {getLocalizedDescription(event)}
                      </Typography>
                      
                      {/* Display comment content if it's a comment event */}
                      {event.description && event.description.includes('Comment added to document') && event.metadata && event.metadata.comment_content && (
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                          <Typography variant="body2" sx={{ 
                            textAlign: 'right', 
                            fontSize: '0.85rem', 
                            lineHeight: 1.5,
                            fontStyle: 'italic',
                            color: '#495057'
                          }}>
                            "{event.metadata.comment_content}"
                          </Typography>
                        </Box>
                      )}
                      
                      {event.metadata && event.metadata.reason && (
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexDirection: 'row-reverse' }}>
                          <Chip
                            label={`${t('limitedadmin:reason')}: ${event.metadata.reason}`}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ fontSize: '0.6rem' }}
                          />
                        </Box>
                      )}
                    </Paper>
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Alert severity="info">
              {t('limitedadmin:no_flow_events_found')}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Event Details Dialog */}
      <Dialog
        open={eventDetailsOpen}
        onClose={handleCloseEventDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'right' }}>
          {t('limitedadmin:event_details')}
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ textAlign: 'right' }}>
                {getLocalizedDescription(selectedEvent)}
              </Typography>
              
              {/* Display comment content in dialog if available */}
              {selectedEvent.description && selectedEvent.description.includes('Comment added to document') && selectedEvent.metadata && selectedEvent.metadata.comment_content && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ textAlign: 'right', color: '#495057', fontWeight: 600 }}>
                    {t('limitedadmin:comment_content', 'Comment Content')}:
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    textAlign: 'right', 
                    fontSize: '0.95rem', 
                    lineHeight: 1.6,
                    fontStyle: 'italic',
                    color: '#212529'
                  }}>
                    "{selectedEvent.metadata.comment_content}"
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexDirection: 'row-reverse' }}>
                <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
                  {t('common:user')}: {selectedEvent.user?.full_name || selectedEvent.user?.username || t('common:system')}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexDirection: 'row-reverse' }}>
                <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
                  {t('common:timestamp')}: {formatDate(selectedEvent.created_at, true)}
                </Typography>
              </Box>
              
              {selectedEvent.metadata && Object.keys(selectedEvent.metadata).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ textAlign: 'right' }}>
                    {t('limitedadmin:metadata')}:
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <pre style={{ margin: 0, fontSize: '0.875rem', textAlign: 'right' }}>
                      {JSON.stringify(selectedEvent.metadata, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          <Button onClick={handleCloseEventDetails}>
            {t('common:close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LimitedAdminDocumentFlowViewer;
